/**
 * Staff sign-up from an admin's SMS link (ADM-01; api-contract.md §2.3).
 *
 * Admins create support/admin/pharmacist accounts (and activate doctors) without a password; the
 * API texts a link to /auth/signup?token=… on this web app. Here the new user chooses a password,
 * the token is consumed (which activates the account and signs them in), and they land on their
 * role's home.
 */

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { consumeSignup } from "../../api/auth";
import { ApiError } from "../../api/errors";
import { usePasswordRules } from "../../api/usePasswordRules";
import { roleHome, useAuth } from "../../auth/AuthContext";
import { PasswordChecklist } from "../../components/ui/PasswordChecklist";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { passesAll } from "../../lib/passwordRules";

// PASSWORD_WEAK is shown in the server's words: they state the rule it applies (AUTH-15).
const MESSAGES: Record<string, string> = {
  TOKEN_INVALID: "This sign-up link isn't valid. Ask your administrator to send a new one.",
  TOKEN_EXPIRED: "This sign-up link has expired. Ask your administrator to send a new one.",
  TOKEN_ALREADY_CONSUMED: "This link has already been used. Sign in with the password you chose.",
  LOGIN_DISABLED: "Sign-in isn't enabled for this account yet. Please contact your administrator.",
};

export function Signup() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { login } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordRules = usePasswordRules();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!passesAll(passwordRules, password)) return setError("Your password doesn't meet the rules below yet.");
    if (password !== confirm) return setError("The two passwords don't match.");
    setError(null);
    setBusy(true);
    try {
      const result = await consumeSignup(token, password);
      login(
        { id: result.user.id, role: result.user.role, full_name: result.user.full_name },
        result.access_token && result.refresh_token
          ? { access: result.access_token, refresh: result.refresh_token }
          : undefined,
      );
      navigate(roleHome(result.user.role), { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (MESSAGES[err.code] ?? err.message)
          : "Couldn't reach the server. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-brand">Set up your account</h1>
        <p className="mb-6 text-sm text-gray-500">Choose the password you'll use to sign in.</p>
        {!token ? (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            This page needs the link from your sign-up SMS. Open that link again, or ask your
            administrator to send a new one.
          </div>
        ) : (
          <>
            {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <form onSubmit={onSubmit} className="space-y-4">
              <PasswordInput
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                required
              />
              <PasswordChecklist password={password} rules={passwordRules} />
              <PasswordInput
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm password"
                required
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-brand py-2 text-brand-fg disabled:opacity-50"
              >
                {busy ? "Setting up…" : "Set password and sign in"}
              </button>
            </form>
          </>
        )}
        <p className="mt-6 text-center text-xs text-gray-400">
          Already set up? <Link to="/" className="text-brand hover:underline">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
